/**
 * The workload bar's colour — a VALIDATED ordinal ramp over existing tokens, both modes.
 *
 * ===========================================================================
 * WHY AN ORDINAL RAMP AND NOT FIVE CATEGORICAL HUES
 * ===========================================================================
 * Assigned → In progress → In testing → Ready is a PIPELINE ORDER: swapping the
 * buckets would change the meaning, so this is an ordinal scale and takes ONE hue in
 * monotone lightness steps (dataviz skill, `color-formula.md` "Categorical or
 * ordinal?"). The stage tokens (`--crf-stage-dev/test/deploy-text`) were tried first
 * as a categorical set and FAILED the six checks outright — MEASURED with
 * `scripts/validate_palette.js`: chroma floor (`#0e7490` C 0.094), CVD separation
 * (`#15803d`↔`#a16207` ΔE 5.0 protan), normal-vision floor (`#5b6cfa`↔`#4338ca` ΔE
 * 14.2). A palette that fails the validator is not shipped on taste.
 *
 * `blocked` (= `testing_failed`, returned work) is a STATE, not a stage: it wears the
 * status WARNING token, never a step of the ramp (the collision rule — status colours
 * are reserved). `unassigned` has no bucket of its own on the bar; its row draws one
 * neutral mark in `text/secondary`, the de-emphasis grey that still clears 3:1 on the
 * surface in both modes (4.64 light / 5.85 dark), so the "Unassigned" row reads as
 * weight without claiming a stage.
 *
 * ===========================================================================
 * THE TWO RAMPS, EVERY HEX AN EXISTING `--crf-*` TOKEN, BOTH VALIDATED `--ordinal`
 * ===========================================================================
 *   LIGHT  (surface #ffffff)  assigned    --crf-accent-violet               #8a8cd9
 *                             in_progress --crf-accent-indigo               #5b6cfa
 *                             in_testing  --crf-stage-dev-text              #4338ca
 *                             ready       --crf-brand-navy                  #1a2a6b
 *     → Lightness monotone PASS · adjacent ΔL ≥ 0.06 PASS · light end 3.07:1 PASS ·
 *       single hue (13°) PASS.
 *   DARK   (surface #1f1f1f)  assigned    --crf-accent-indigo-strong-active #4553c4
 *                             in_progress --crf-accent-indigo               #5b6cfa
 *                             in_testing  --crf-accent-violet               #8a8cd9
 *                             ready       --crf-accent-indigo-soft          #eef0fe
 *     → monotone PASS · ΔL PASS · dark end 2.58:1 vs surface PASS · single hue (9°) PASS.
 * The dark ramp is the same hue re-stepped for the dark surface with the anchor
 * flipped (more-advanced = lighter on dark), which is what the skill prescribes —
 * "dark mode is SELECTED, its own steps, not an automatic flip". The alternative dark
 * orderings were run too and FAILED (`#1a2a6b` at 1.25:1 on the dark surface; a
 * `--crf-accent-sky` light end spans 49° of hue). `team.contrast.test.ts` pins every
 * hex below to tokens.css and re-checks lightness order and surface contrast, so a
 * token nudge that breaks the ramp reddens by name.
 *
 * ===========================================================================
 * WHY THESE ARE LITERAL CLASS STRINGS
 * ===========================================================================
 * Tailwind v4's scanner reads class names out of SOURCE; a class assembled at runtime
 * is never generated. `bg-(--crf-…)` is the v4 shorthand for `bg-[var(--crf-…)]`, and
 * `dark:` is the `data-theme` variant `app.css` declares. Each bucket's light and dark
 * fill therefore sit side by side in one string, and the contrast test parses them
 * back out with a regex rather than trusting this comment.
 */

import type { WorkloadBucket } from './team-api';

/** The bar segment's fill per bucket — light class, then its dark twin. */
export const BUCKET_FILL: Readonly<Record<WorkloadBucket, string>> = {
  assigned: 'bg-(--crf-accent-violet) dark:bg-(--crf-accent-indigo-strong-active)',
  in_progress: 'bg-(--crf-accent-indigo) dark:bg-(--crf-accent-indigo)',
  in_testing: 'bg-(--crf-stage-dev-text) dark:bg-(--crf-accent-violet)',
  /* The STATUS warning token, both modes — returned work is a state, not a stage. */
  blocked: 'bg-(--crf-status-warning-text) dark:bg-(--crf-status-warning-text)',
  ready: 'bg-(--crf-brand-navy) dark:bg-(--crf-accent-indigo-soft)',
};

/** The Unassigned row's single neutral mark — see the header. */
export const UNASSIGNED_FILL = 'bg-fg-secondary';

/**
 * The pipeline steps, in ramp order, for the contrast test's monotonicity check. `blocked`
 * is deliberately absent: it is not a step of the ramp.
 */
export const RAMP_ORDER = ['assigned', 'in_progress', 'in_testing', 'ready'] as const;
